/**
 * Bayram bezaklari: ekran ostidagi naqsh qatlami va bosh ekrandagi
 * tabrik lentasi.
 *
 * NEGA SVG, RASM EMAS
 * -------------------
 * Naqsh butun ekranni qoplaydi va har bir qurilmada boshqa o'lchamga
 * cho'ziladi. PNG bunda yo bulanadi, yo og'irlashadi. SVG vektor:
 * `slice` bilan toza cho'ziladi, `currentColor` orqali mavzuga
 * bo'ysunadi va gzip'dan keyin bir necha yuz bayt bo'ladi.
 *
 * QATLAM TARTIBI
 * --------------
 *   body::before (gradient)  ->  .holiday-decor (naqsh)  ->  kartalar
 *
 * Ikkalasi ham `z-index: -1`, shuning uchun shisha kartaning
 * `backdrop-filter` i IKKALASINI ham bulaydi: naqsh karta ostida
 * yumshaydi, kartalar orasidagi bo'shliqda esa aniq ko'rinadi. Aynan
 * shu — glassmorphism naqsh bilan birga ishlaganda ma'noga kiradi.
 *
 * KONTRAST — BU YERDA EHTIYOT BO'LING
 * -----------------------------------
 * Naqsh fonni YORITADI, fon esa shisha karta ostidagi matnning
 * taglig'i. Ya'ni bezakning shaffofligi to'g'ridan-to'g'ri o'qilishga
 * ta'sir qiladi va bu ko'z bilan sezilmaydi.
 *
 * BUDJET: gradient + bezak + shisha yig'indisi bayramsiz fonning eng
 * yorug' nuqtasidan (L = 0.0968) oshmasligi kerak. Shunda ikkilamchi
 * matn 4.7:1 atrofida qoladi — AA chegarasi 4.5:1.
 *
 * Shu sabab bezakda YORUG' PARDA YO'Q: ilgari Mustaqillik va Qish
 * mavzularida yuqorida oq gradient bor edi, u aynan fonning eng
 * yorug' joyiga tushardi va ikkilamchi matnni 3.4:1 ga tushirardi.
 * Endi u yerda faqat chiziqli shakllar bor.
 *
 * Shaffoflikni oshirsangiz `web/test/holidays.test.mjs` dagi
 * "bayram foni matn kontrastini yemaydi" testi buni ushlaydi.
 *
 * HARAKAT
 * -------
 * Sekin, ambient va FAQAT `transform`/`opacity` — reflow yo'q, GPU da
 * ketadi. `prefers-reduced-motion` da butunlay o'chadi (styles.css).
 */

import type { ReactNode } from "react";
import type { Holiday } from "./holidays";

/* ------------------------------------------------------------ yordamchi */

/** Besh burchakli yulduz yo'li. `r` — tashqi radius. */
export function star(cx: number, cy: number, r: number): string {
  const inner = r * 0.382;
  let d = "";
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    d += `${i ? "L" : "M"}${(cx + rad * Math.cos(a)).toFixed(1)} ${(cy + rad * Math.sin(a)).toFixed(1)}`;
  }
  return `${d}Z`;
}

/**
 * Yarim oy: ikkita aylana `evenodd` qoidasi bilan — kattasidan
 * siljitilgan kichigi "kesib olinadi". Bitta yo'l, mask kerak emas.
 */
export function crescent(cx: number, cy: number, r: number, bite = 0.78): string {
  const ir = r * 0.86;
  const dx = r * bite;
  const arc = (x: number, y: number, rr: number) =>
    `M${x - rr} ${y}a${rr} ${rr} 0 1 0 ${rr * 2} 0a${rr} ${rr} 0 1 0 ${-rr * 2} 0Z`;
  return arc(cx, cy, r) + arc(cx + dx, cy, ir);
}

/**
 * Uzluksiz to'lqin chizig'i: bir davri 390px (ekran kengligi), jami
 * uch davr (-390 dan 780 gacha). Guruh 390px ga surilganda naqsh
 * o'ziga aynan tushadi — shuning uchun animatsiya chokini ko'rsatmaydi.
 */
function ripple(y: number, amp: number): string {
  let d = `M-390 ${y}Q-292.5 ${y - amp} -195 ${y}`;
  for (let x = 0; x <= 780; x += 195) d += `T${x} ${y}`;
  return d;
}

/** To'liq ekranli bezak lavhasi. */
function Sky({ children }: { children: ReactNode }) {
  return (
    <svg
      className="hd-svg"
      viewBox="0 0 390 800"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/**
 * Siyrak yulduzlar — qo'lda tanlangan koordinatalar.
 * Tasodifiy son ATAYLAB ishlatilmadi: har qayta chizishda joyi
 * o'zgarib turadigan bezak ko'zga tashlanadi va testda ham
 * takrorlanmaydi.
 */
const DUST: [number, number, number][] = [
  [36, 58, 2.6], [118, 32, 1.8], [214, 76, 2.2], [292, 44, 1.6],
  [352, 108, 2.4], [64, 168, 1.7], [176, 148, 2.1], [330, 196, 1.9],
  [24, 268, 2.3], [246, 254, 1.6], [368, 312, 2.0], [92, 350, 1.8],
  [188, 404, 2.4], [318, 438, 1.7], [46, 470, 2.0], [268, 546, 1.9],
  [140, 604, 2.2], [356, 646, 1.6], [78, 690, 2.1], [206, 744, 1.8],
];

function Dust({ opacity = 0.5 }: { opacity?: number }) {
  return (
    <g fill="currentColor" opacity={opacity}>
      {DUST.map(([x, y, r], i) => (
        <path
          key={i}
          className={`hd-twinkle hd-d${i % 4}`}
          d={star(x, y, r * 1.9)}
        />
      ))}
    </g>
  );
}

/* --------------------------------------------------- Mustaqillik kuni */

/*
 * Bayroq ranglari QUYUQ FON UCHUN OCHARTIRILGAN. Rasmiy qiymatlar
 * (#0099B5, #1EB53A, #CE1126) navy ustida 0.13 shaffoflikda deyarli
 * ko'rinmay ketadi. Ohang saqlandi, yorqinlik ko'tarildi — quyuq
 * mavzuda brend ranglarini shunday moslash odatiy qoida.
 */
export const FLAG_BLUE = "#3fb6e0";
export const FLAG_WHITE = "#ffffff";
export const FLAG_GREEN = "#46d47c";
export const FLAG_RED = "#ff7a72";

function Independence() {
  /* Bayroqdagi 12 yulduz: 3 + 4 + 5 qatorda, chapdan tekislangan */
  const rows = [3, 4, 5];
  const stars: ReactNode[] = [];
  rows.forEach((count, row) => {
    for (let i = 0; i < count; i++) {
      stars.push(
        <path key={`${row}-${i}`} d={star(150 + i * 26, 62 + row * 30, 8.5)} />,
      );
    }
  });

  return (
    <Sky>
      {/*
        Bayroq gerbi: yarim oy + 12 yulduz. Kompozitsiyaning tayanchi.
        Ilgari uning ortida oq parda ham bor edi — u gradientning eng
        yorug' joyiga tushib, karta ostidagi ikkilamchi matnni 3.4:1 ga
        tushirardi. Gradient bu yorug'likni allaqachon beradi.
      */}
      <g fill={FLAG_WHITE} opacity="0.1">
        <path d={crescent(86, 92, 54)} fillRule="evenodd" />
        <g className="hd-twinkle hd-d1">{stars}</g>
      </g>

      <Dust opacity={0.34} />

      {/*
        Hilpiragan bayroq: uchta qalin to'lqin chiziq, orasida ingichka
        qizil hoshiya — Davlat bayrog'ining aynan tuzilishi.
        Guruh bir davrga suriladi va uzluksiz "shamolda" ko'rinadi.
      */}
      <g className="hd-ribbon" fill="none" strokeLinecap="butt">
        <g opacity="0.13">
          <path d={ripple(556, 26)} stroke={FLAG_BLUE} strokeWidth="46" />
          <path d={ripple(604, 26)} stroke={FLAG_WHITE} strokeWidth="46" />
          <path d={ripple(652, 26)} stroke={FLAG_GREEN} strokeWidth="46" />
        </g>
        <g opacity="0.3" stroke={FLAG_RED} strokeWidth="3">
          <path d={ripple(580, 26)} />
          <path d={ripple(628, 26)} />
        </g>
      </g>
    </Sky>
  );
}

/* ----------------------------------------------- Ramazon / Qurbon hayit */

/** Osilgan fonus (hayit chirog'i). */
function Lantern({ x, y, s, cls }: { x: number; y: number; s: number; cls: string }) {
  return (
    <g className={cls} transform={`translate(${x} ${y})`}>
      {/* Arqon ekranning tepasidan tushadi — fonus osilgani ko'rinsin */}
      <path d={`M0 ${-y} V-14`} stroke="currentColor" strokeWidth="1.1" fill="none" opacity="0.5" />
      <g transform={`scale(${s})`}>
        <circle cy="20" r="42" fill="url(#hd-eid-lamp)" />
        <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
          <path d="M-5 -14h10" />
          <path d="M-8 -9h16v7h-16z" />
          <path d="M-9 -1C-18 9-18 29-9 39h18C18 29 18 9 9 -1Z" />
          <path d="M-4 1C-8 11-8 27-4 37M4 1C8 11 8 27 4 37M0 -1V39" opacity="0.6" />
          <path d="M-6 39h12l-2.5 7h-7z" />
          <path d="M0 46v4" />
        </g>
        <circle cy="52" r="2.4" fill="currentColor" />
      </g>
    </g>
  );
}

function Eid() {
  return (
    <Sky>
      <defs>
        {/*
          Xatam — sakkiz burchakli yulduz: bitta kvadrat va uning
          45° burilgan nusxasi. Islomiy geometriyaning eng keng
          tarqalgan naqshi, cheksiz takrorlanadi.
        */}
        <pattern id="hd-khatam" width="58" height="58" patternUnits="userSpaceOnUse">
          <g fill="none" stroke="currentColor" strokeWidth="1">
            <rect x="15" y="15" width="28" height="28" />
            <rect x="15" y="15" width="28" height="28" transform="rotate(45 29 29)" />
            <circle cx="29" cy="29" r="3.4" />
            <path d="M0 0h5M0 0v5M58 0h-5M58 0v5M0 58h5M0 58v-5M58 58h-5M58 58v-5" />
          </g>
        </pattern>
        <radialGradient id="hd-eid-lamp">
          <stop offset="0%" stopColor="#ffd166" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#ffd166" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Naqsh butun ekranda — mato yoki koshin hissi */}
      <rect width="390" height="800" fill="url(#hd-khatam)" color="#7fe3d8" opacity="0.08" />

      {/* Hilol va yulduz: hayitning ramzi */}
      <g fill="#ffd166" opacity="0.13">
        <path d={crescent(304, 108, 46)} fillRule="evenodd" />
        <path className="hd-twinkle" d={star(236, 84, 15)} />
      </g>

      <Dust opacity={0.3} />

      {/* Uchta fonus har xil balandlikda — ular sekin tebranadi */}
      <g color="#ffd166" opacity="0.5">
        <Lantern x={54} y={126} s={1} cls="hd-sway hd-d0" />
        <Lantern x={148} y={72} s={0.72} cls="hd-sway hd-d2" />
        <Lantern x={338} y={210} s={0.86} cls="hd-sway hd-d1" />
      </g>

      {/*
        Masjid siluetlari — piyozsimon gumbaz va ikkita minora.
        Eng pastda, tabbar tepasida turadi.
      */}
      <g fill="#8ff0e2" opacity="0.1" transform="translate(0 726)">
        <path d="M-6 0v-56h9v56zM-9 -56C-9 -66 6 -66 6 -56ZM-1.5 -66v-10h3v10z" transform="translate(74)" />
        <path d="M-30 0C-30 -22-13 -27 0 -50C13 -27 30 -22 30 0ZM-2 -50v-13h4v13z" transform="translate(150)" />
        <path d={crescent(150, -70, 8)} fillRule="evenodd" />
        <path d="M-38 0h76v14h-76z" transform="translate(150)" />
        <path d="M-6 0v-64h9v64zM-9 -64C-9 -74 6 -74 6 -64ZM-1.5 -74v-10h3v10z" transform="translate(232)" />
        <path d="M-24 0C-24 -17-10 -21 0 -38C10 -21 24 -17 24 0Z" transform="translate(306)" />
      </g>
    </Sky>
  );
}

/* ------------------------------------------------------------- Navro'z */

/** Lola — Navro'zning va O'zbekiston bahorining ramzi. */
export const TULIP =
  "M0 -2C-11 -4-14 -16-11 -28C-8 -19-5 -15-1.6 -14L-1.6 -31C-0.6 -34 0.6 -34 1.6 -31" +
  "L1.6 -14C5 -15 8 -19 11 -28C14 -16 11 -4 0 -2Z";

/** Bug'doy niholi — sumalak uchun undiriladigan maysa. */
const SPROUT =
  "M0 0C1 -12 3 -20 1.5 -30M1 -9C-5 -11-8 -16-7 -20C-3 -18-0.5 -13 1 -9Z" +
  "M1.6 -17C7 -19 10 -24 9 -28C5 -26 3 -21 1.6 -17Z";

/** Besh bargli gul (o'rik guli) — bahorda birinchi bo'lib ochiladi. */
function Blossom({ r = 1 }: { r?: number }) {
  return (
    <g transform={`scale(${r})`}>
      {[0, 72, 144, 216, 288].map((a) => (
        <ellipse key={a} cx="0" cy="-6.5" rx="4" ry="6.5" transform={`rotate(${a})`} />
      ))}
      <circle r="2.4" opacity="0.7" />
    </g>
  );
}

function Navruz() {
  return (
    <Sky>
      <defs>
        {/*
          Islimi — cheksiz o'ralib ketuvchi novda. O'zbek amaliy
          san'atining asosiy naqshi; bu yerda ekranning pastida
          gulzor chegarasi vazifasini bajaradi.
        */}
        <pattern id="hd-islimi" width="130" height="64" patternUnits="userSpaceOnUse">
          <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
            <path d="M0 44C22 44 26 12 46 12C62 12 64 40 82 40C100 40 104 16 130 16" />
            <path d="M40 20C34 14 34 6 40 2C44 8 44 16 40 20Z" />
            <path d="M86 34C92 28 92 20 86 16C82 22 82 30 86 34Z" />
            <circle cx="63" cy="30" r="2.6" />
          </g>
        </pattern>
        <linearGradient id="hd-nav-glow" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#48c482" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#48c482" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Pastdan ko'tarilgan yashillik — maysa hissi */}
      <rect x="0" y="520" width="390" height="280" fill="url(#hd-nav-glow)" />

      {/* Uchib yurgan gul barglari — bahor shamoli */}
      <g fill="#ffb3c9" opacity="0.34">
        <g className="hd-drift hd-d0" transform="translate(58 150)"><Blossom r={1.15} /></g>
        <g className="hd-drift hd-d2" transform="translate(300 96)"><Blossom r={0.8} /></g>
        <g className="hd-drift hd-d1" transform="translate(196 300)"><Blossom r={1} /></g>
        <g className="hd-drift hd-d3" transform="translate(342 402)"><Blossom r={0.72} /></g>
        <g className="hd-drift hd-d2" transform="translate(30 470)"><Blossom r={0.9} /></g>
      </g>

      <Dust opacity={0.24} />

      {/* Sumalak maysalari — butun kenglik bo'ylab */}
      <g fill="#6fe0a4" opacity="0.16" transform="translate(0 748)">
        {[14, 52, 96, 138, 186, 228, 274, 316, 360].map((x, i) => (
          <g key={x} transform={`translate(${x}) scale(${1 + (i % 3) * 0.22})`}>
            <path d={SPROUT} />
          </g>
        ))}
      </g>

      {/* Lolazor — gulchambar pastki chap burchakda */}
      <g opacity="0.2" transform="translate(0 704)">
        {[[46, 1.5, -8], [92, 1.1, 6], [128, 1.3, -4], [312, 1.2, 7], [352, 0.95, -6]].map(
          ([x, s, rot], i) => (
            <g key={i} transform={`translate(${x}) rotate(${rot}) scale(${s})`}>
              <path d={TULIP} fill="#ff9ab4" />
              <path d="M0 -2V44" stroke="#6fe0a4" strokeWidth="2" fill="none" />
              <path d="M0 16C-9 12-13 4-12 -2C-6 2-2 9 0 16Z" fill="#6fe0a4" />
            </g>
          ),
        )}
      </g>

      {/* Islimi hoshiyasi eng pastda */}
      <rect
        x="-20"
        y="756"
        width="430"
        height="64"
        fill="url(#hd-islimi)"
        color="#7fe3b0"
        opacity="0.16"
      />
    </Sky>
  );
}

/* ----------------------------------------------------------- Yangi yil */

function Winter() {
  /*
   * Uchta chuqurlikda qor: kattalari oldinda va tezroq, kichiklari
   * orqada va sekinroq. Parallaks hissi shundan chiqadi.
   */
  const flakes: [number, number, number, number][] = [
    [40, 90, 1.25, 0], [156, 44, 0.8, 2], [268, 128, 1.05, 1], [352, 60, 0.7, 3],
    [92, 236, 0.9, 2], [212, 288, 1.2, 0], [330, 340, 0.75, 3], [26, 396, 1.0, 1],
    [174, 452, 0.85, 3], [296, 508, 1.15, 2], [64, 560, 0.7, 0], [244, 632, 0.95, 1],
  ];

  return (
    <Sky>
      <defs>
        {/* Bitta nur — qolgan beshtasi burilgan nusxasi (60° qadam) */}
        <g id="hd-arm">
          <path d="M0 0V-15M0 -6l-3.6 -3.6M0 -6l3.6 -3.6M0 -10.8l-2.7 -2.7M0 -10.8l2.7 -2.7" />
        </g>
        <g id="hd-flake" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <use key={a} href="#hd-arm" transform={`rotate(${a})`} />
          ))}
        </g>
      </defs>

      {/* Yuqoridagi oq parda OLIB TASHLANDI — sabab Mustaqillikdagi bilan
          bir xil: u gradientning eng yorug' joyini yana yoritardi. */}
      <g color="#d5e7ff" opacity="0.18">
        {flakes.map(([x, y, s, d], i) => (
          <use
            key={i}
            href="#hd-flake"
            className={`hd-fall hd-d${d}`}
            transform={`translate(${x} ${y}) scale(${s})`}
          />
        ))}
      </g>

      <Dust opacity={0.3} />

      {/* Archazor — quyuq siluet, tabbar tepasida */}
      <g fill="#a8ccf5" opacity="0.12" transform="translate(0 752)">
        {[[22, 1.25], [70, 0.85], [118, 1.05], [286, 0.95], [330, 1.3], [372, 0.8]].map(
          ([x, s], i) => (
            <g key={i} transform={`translate(${x}) scale(${s})`}>
              <path d="M0 0h-14l7-9h-4l6-9h-3L0 -28l8 10h-3l6 9h-4l7 9H0z" />
              <path d="M-2.4 0h4.8v6h-4.8z" />
            </g>
          ),
        )}
      </g>
    </Sky>
  );
}

/* ---------------------------------------------------------- birlashtirish */

const DECOR: Record<string, () => ReactNode> = {
  independence: Independence,
  eid: Eid,
  navruz: Navruz,
  winter: Winter,
};

/**
 * Ekran ostidagi bezak qatlami. Bayram bo'lmasa hech narsa
 * chizilmaydi — DOM ham toza qoladi.
 */
export function HolidayDecor({ holiday }: { holiday: Holiday | null }) {
  const Decor = holiday ? DECOR[holiday.theme] : undefined;
  if (!Decor) return null;
  return (
    <div className="holiday-decor" aria-hidden="true">
      <Decor />
    </div>
  );
}
